import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import CenteredContent from '@cohort/wallet/components/layout/CenteredContent';
import SelectDigitalAssetModal from '@cohort/wallet/components/modals/perks/SelectDigitalAssetModal';
import AboutPerkPanel from '@cohort/wallet/components/space/AboutPerkPanel';
import useScreenSize from '@cohort/wallet/hooks/useScreenSize';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import SpacePage from '@cohort/wallet/layouts/space/SpacePage';
import {getMerchantSpaceRoute, getPerkUsageRoute} from '@cohort/wallet/lib/Pages';
import type {AccessesAndOwnership} from '@cohort/wallet/lib/Utils';
import {getPerkIntancesAndOwnership} from '@cohort/wallet/lib/Utils';
import PerkDetailPrivateContent from '@cohort/wallet/pages/space/PerkDetailPage/PerkDetailPrivateContent';
import type {PerkWDto} from '@cohort/wallet-schemas/perk';
import type {PerkAccessWDto} from '@cohort/wallet-schemas/perkAccess';
import {Clock, Fire, ThumbsDown} from '@phosphor-icons/react';
import React, {Fragment, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {match} from 'ts-pattern';

/*
  @Devs TODO: Rename instances into accesses.
*/

const PerkDetailPageContentWrapper: React.FC<{children: React.ReactNode}> = ({children}) => (
  <div className="flex min-h-full flex-col space-y-8">{children}</div>
);

const PerkDetailPage: React.FC = () => {
  const {space, setTransitioning} = useSpaceContext();
  const {t} = useTranslation('pages', {keyPrefix: 'space.perkDetailPage'});
  const navigate = useNavigate();
  const {isMobile} = useScreenSize();
  const {perkId} = useParams();
  const [queryParameters] = useSearchParams();
  const [perk, setPerk] = useState<PerkWDto>();
  const [accessesAndOwnerships, setAccessesAndOwnerships] = useState<AccessesAndOwnership[]>([]);
  const [modalOpened, setModalOpened] = useState(false);

  const urlInstanceAndOwnership = accessesAndOwnerships.find(
    e => e.ownership.digitalAsset.id === queryParameters.get('digitalAssetId')
  );

  const firstInstanceAndOwnership =
    accessesAndOwnerships[0] !== undefined ? accessesAndOwnerships[0] : undefined;

  const accessAndOwnership = urlInstanceAndOwnership ?? firstInstanceAndOwnership;

  useEffect(() => {
    if (space === undefined) {
      return;
    }
    const spacePerk = space.perks.find(p => p.perk.id === perkId);
    if (spacePerk === undefined) {
      navigate(getMerchantSpaceRoute(), {replace: true});
      return;
    }
    const {perk, instances} = spacePerk;
    setPerk(perk);
    setAccessesAndOwnerships(getPerkIntancesAndOwnership(space, instances));
  }, [navigate, perkId, space]);

  const handleOnUsePerkClicked = (): void => {
    if (perk === undefined || accessAndOwnership === undefined) return;
    if (urlInstanceAndOwnership !== undefined || accessesAndOwnerships.length === 1) {
      // cases where the select digital asset modal is skipped:
      setTransitioning({active: true, enterFrom: 'left'});
      return navigate(getPerkUsageRoute(accessAndOwnership.ownership.id, perk.id));
    } else {
      // case where the select digital asset modal is shown:
      setModalOpened(true);
    }
  };

  const getUsePerkBtnLabel = (access: PerkAccessWDto): string => {
    if (access.raffleStatus !== null) {
      return access.statusMessage;
    }
    return access.canBeUsed ? t('usePerk') : t('alreadyUsed');
  };

  if (perk === undefined || accessAndOwnership === undefined) {
    return <Fragment />;
  }

  const showUsePerkButton =
    perk.integration !== null || accessAndOwnership.access.raffleStatus !== null;
  const disableUsePerkButton =
    accessAndOwnership.access.canBeUsed === false || perk.integration === null;
  const isPerkEmpty = !perk.privateContentFileType && !perk.privateContent;

  const buttonIcon = match(accessAndOwnership.access.raffleStatus)
    .with('won', () => <Fire className="h-5 w-5" />)
    .with('lost', () => <ThumbsDown className="h-5 w-5" />)
    .with('pending', () => <Clock className="h-5 w-5" />)
    .with(null, () => undefined)
    .exhaustive();
  const ContentWrapper = isMobile && isPerkEmpty ? CenteredContent : PerkDetailPageContentWrapper;
  const AboutPerkPanelComponent = (
    <AboutPerkPanel
      perk={perk}
      accessesAndOwnerships={accessesAndOwnerships}
      orientation={!isMobile && isPerkEmpty ? 'horizontal' : 'vertical'}
    >
      {showUsePerkButton && (
        <PrimaryButton
          onClick={handleOnUsePerkClicked}
          text={getUsePerkBtnLabel(accessAndOwnership.access)}
          type="submit"
          icon={buttonIcon}
          testId="perk-card-button"
          disabled={disableUsePerkButton}
          tracking={{namespace: 'perks.detail.use'}}
        />
      )}
    </AboutPerkPanel>
  );

  return (
    <SpacePage
      title={perk.displayName}
      tracking={{
        namespace: 'perks.detail',
        metadata: {
          appId: perk.integration?.perkIntegrationId,
          perkId: perk.id,
          digitalAssetId: accessAndOwnership.ownership.digitalAsset.id,
          ownershipId: accessAndOwnership.ownership.id,
          accessId: accessAndOwnership.access.id,
        },
      }}
      leftPanel={AboutPerkPanelComponent}
      panelIsContent={isPerkEmpty}
    >
      <ContentWrapper>
        {isMobile && AboutPerkPanelComponent}

        <PerkDetailPrivateContent
          ownershipId={accessesAndOwnerships[0]?.ownership.id ?? ''}
          perkAccessId={accessesAndOwnerships[0]?.access.id ?? ''}
          perk={perk}
        />
      </ContentWrapper>

      <SelectDigitalAssetModal
        accessesAndOwnerships={accessesAndOwnerships}
        modalOpened={modalOpened}
        setModalOpened={setModalOpened}
      />
    </SpacePage>
  );
};

export default PerkDetailPage;
